/**
 * The playback scrubber.
 *
 * While a drag is in progress the bar shows the preview position rather than
 * the transport's, and the record's progress ring follows the finger — the
 * seek itself only happens on release.
 */

import type { Component } from 'solid-js';
import { seekBy, seekToFraction } from '../../app/commands.ts';
import { hardwareOrNull } from '../../app/hardware.ts';
import { fmtTime } from '../../lib/format.ts';
import { clamp } from '../../lib/math.ts';
import * as player from '../../state/player.ts';
import styles from './Scrubber.module.css';

const STEP = 5;
const STEP_BIG = 30;

export const Scrubber: Component = () => {
  let root!: HTMLDivElement;
  let bar!: HTMLDivElement;
  let pointer: number | null = null;

  const fractionAt = (clientX: number): number => {
    const r = bar.getBoundingClientRect();
    return clamp((clientX - r.left) / r.width, 0, 1);
  };

  const preview = (clientX: number): void => {
    const f = fractionAt(clientX);
    player.setScrubPosition(f * player.duration());
    hardwareOrNull()?.tt.setProgress(f);
  };

  const onDown = (e: PointerEvent): void => {
    if (!player.duration()) return;
    pointer = e.pointerId;
    player.setScrubbing(true);
    root.setPointerCapture(e.pointerId);
    preview(e.clientX);
    e.preventDefault();
  };

  const onMove = (e: PointerEvent): void => {
    if (!player.scrubbing() || e.pointerId !== pointer) return;
    preview(e.clientX);
  };

  const onUp = (e: PointerEvent): void => {
    if (!player.scrubbing() || (pointer !== null && e.pointerId !== pointer)) return;
    const f = fractionAt(e.clientX);
    player.setScrubbing(false);
    pointer = null;
    seekToFraction(f);
  };

  const onCancel = (): void => {
    player.setScrubbing(false);
    pointer = null;
  };

  /**
   * The same arrows work anywhere in the app, so the focused scrubber has to
   * claim the event — otherwise it would seek once here and once globally.
   */
  const onKeyDown = (e: KeyboardEvent): void => {
    const step = e.shiftKey ? STEP_BIG : STEP;
    if (e.key === 'ArrowRight') seekBy(step);
    else if (e.key === 'ArrowLeft') seekBy(-step);
    else return;
    e.preventDefault();
    e.stopPropagation();
  };

  const percent = (): string => `${player.progress() * 100}%`;
  const label = (): string =>
    `${fmtTime(player.displayPosition())} of ${fmtTime(player.duration())}`;

  return (
    <div
      ref={root}
      class={[styles.scrub, { [styles.dragging as string]: player.scrubbing() }]}
      role="slider"
      tabindex="0"
      aria-label="Playback position"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={String(Math.round(player.progress() * 100))}
      aria-valuetext={label()}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onCancel}
      onKeyDown={onKeyDown}
    >
      <span class={styles.time} data-testid="time-current">
        {fmtTime(player.displayPosition())}
      </span>
      <div class={styles.bar} data-testid="scrub-bar" ref={bar}>
        <div class={styles.fill} style={{ width: percent() }} />
        <div class={styles.knob} style={{ left: percent() }} />
      </div>
      <span class={[styles.time, styles.duration]} data-testid="time-duration">
        {player.duration() ? fmtTime(player.duration()) : '--:--'}
      </span>
    </div>
  );
};
